import * as CSS from 'csstype'
import { Button } from 'blaze-ui/Button'
import Styles from './ChooseTemplate.module.scss'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { Selection } from '@react-types/shared'
import { useMobileTemplatePreviewDimensions } from 'components/Wizards/MobileWizards/hooks/useMobileTemplatePreviewDimensions'
import { TemplatesGrid } from './TemplatesGrid'
import { DesignChannel, getAspectRatioKeyFromDesignChannel } from 'components/NewFileModal/Types'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { useField } from 'formik'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { TemplateDrawer } from 'components/Templates/TemplateDrawer'
import { TemplatesDrawerV2 } from 'components/Templates/TemplatesModalV2/TemplatesDrawerV2'
import { noop } from 'lodash'
import { useBasicVisualElementsAndVisualElementsByDescription } from 'hooks/useBasicVisualElementsAndVisualElementsByDescription'
import { VisualElement, VisualElementsClient } from 'api/clients/VisualElementClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useState } from 'react'
import { NativeArchetypeItem } from 'components/Wizards/DesignWizard/TemplateAndCaption/TemplateField'
import { ourUuid } from 'utils'
import { useNativeLayouts } from 'components/Wizards/hooks/useNativeLayouts'

interface SelectTemplateProps {
  item: VisualElement | NativeArchetypeItem
  newStack?: boolean
}

export const ChooseTemplate = observer(
  multiStep(({ schema, submit }) => {
    const { meta } = schema.describe()
    const { drawersStore } = useRootStore()
    const wizardSchema = useWizardSchema() as MultiStepSchema
    const channel = wizardSchema.meta.channel as DesignChannel
    const [{ value: topic }] = useField('topic')
    const [{ value: width }] = useField('width')
    const [{ value: height }] = useField('height')
    const [{ value: initialMediaFile }] = useField('initial_media_file_id')
    const [{ value: removeBackground }] = useField('initial_media_file_remove_background')
    const [{ value: isVideo }] = useField('is_video')
    const [, , { setValue: setTemplateId }] = useField('source_visual_element_id')
    const [, , { setValue: setArchetype }] = useField('archetype')
    const [{ value: brand_kit_id }] = useField('brand_kit_id')
    const nativeLayouts = useNativeLayouts(brand_kit_id)
    const { enabled: nativeLayoutsEnabled, archetypes } = nativeLayouts[wizardSchema.meta.channel as DesignChannel]

    const { itemWidth, itemHeight, aspectRatio, items, twoByTwo } = useMobileTemplatePreviewDimensions({
      width,
      height,
    })
    const aspectRatioKey = getAspectRatioKeyFromDesignChannel(channel, width, height)

    const [nativeArchetypes] = useState<NativeArchetypeItem[]>(() =>
      Array.from({ length: nativeLayoutsEnabled ? items : 0 }, (_, i) => ({
        id: ourUuid(),
        isNativeLayout: true,
        archetype: archetypes[i % archetypes.length],
      })),
    )

    const style: CSS.Properties = {
      ['--preview-width' as any]: `${itemWidth}px`,
      ['--preview-height' as any]: `${itemHeight}px`,
    }

    const filters = {
      element_type: 'Template' as 'Template',
      width,
      height,
      styles: 'all' as Selection,
      categories: 'all' as Selection,
      types: isVideo ? new Set(['video']) : ('all' as Selection),
    }

    const templatesQuery = useBasicVisualElementsAndVisualElementsByDescription(
      {
        width,
        height,
        description: topic || '',
        basicLayoutItems: items,
        has_replaceable: !!initialMediaFile,
        replaceable_has_background: !removeBackground,
        byDescriptionItems: items,
        type: isVideo ? ['video'] : undefined,
        additional_seed: (Math.random() + 1).toString(36).substring(2),
      },
      {
        enabled: !!topic && !nativeLayoutsEnabled,
        cacheTime: 2 * 1000 * 60,
        staleTime: Infinity,
      },
    )
    const templates = templatesQuery.data?.visual_elements || []
    const templatesToDisplay = (nativeLayoutsEnabled ? nativeArchetypes : templates).slice(0, items)

    const handleSubmit = async (item?: VisualElement | NativeArchetypeItem) => {
      if (!item) {
        submit()
        return
      }

      if ('isNativeLayout' in item) {
        setTemplateId(undefined)
        setArchetype(item.archetype)
      } else {
        setTemplateId(item.id)
        setArchetype(undefined)
      }
      submit()
    }

    const handleSelect = async ({ item, newStack = false }: SelectTemplateProps) => {
      if (('basic_layout' in item && item.basic_layout) || 'isNativeLayout' in item) {
        handleSubmit(item)
        return
      }

      drawersStore.openDrawer(
        TemplateDrawer,
        {
          initialTemplateId: item.id as number,
          onPressOutside: noop,
          initialAspectRatioKey: aspectRatioKey,
          isAspectRatioKeyEditable: false,
          onConfirm: () => {
            drawersStore.closeStack()
            handleSubmit(item)
          },
        },
        { newStack },
      )
    }

    const handleSeeAll = () => {
      drawersStore.openDrawer(
        TemplatesDrawerV2,
        {
          designChannel: wizardSchema.meta.channel as DesignChannel,
          disabledFilters: isVideo ? ['format', 'types'] : ['format'],
          onUse: () => {},
          onSelect: async (templateId: number) => {
            const item = await VisualElementsClient.getElement(templateId)
            if (item.type === SUCCESS_RESPONSE_TYPE) {
              handleSelect({ item: item.data })
              return
            }
          },
          ...filters,
        },
        { newStack: true },
      )
    }

    return (
      <MobileWizard.Content>
        <MobileWizard.Section>
          <MobileWizard.Title>{meta.title}</MobileWizard.Title>
        </MobileWizard.Section>
        <div className={Styles.templatesContainer} style={style}>
          <TemplatesGrid
            onSelect={(item) => {
              if ('isNativeLayout' in item) {
                handleSelect({ item, newStack: true })
              } else {
                handleSelect({ item, newStack: true })
              }
            }}
            templates={templatesToDisplay}
            isLoading={templatesQuery.isLoading}
            aspectRatio={aspectRatio}
            twoByTwo={twoByTwo}
          />
          <Button variant="tertiary" size="lg" fullWidth onPress={handleSeeAll}>
            See All Templates
          </Button>
          <Button size="lg" variant="ghost" fullWidth onPress={() => handleSubmit()}>
            Skip
          </Button>
        </div>
      </MobileWizard.Content>
    )
  }),
)
